import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';
import { SibillSocket, SibillSocketChannel, createSocket } from '~/sockets/sibill-socket';

const SibillSocketContext = React.createContext<{
  connect: (token: string) => Promise<boolean>;
  disconnect: () => Promise<boolean> | undefined;
  joinChannel: (channelName: string, params?: object) => SibillSocketChannel | undefined;
}>({
  connect: () => Promise.resolve(false),
  disconnect: () => Promise.resolve(false),
  joinChannel: () => {
    throw new Error('Cannot join any channel yet.');
  },
});

export const SibillSocketProvider = ({ children }: PropsWithChildren) => {
  const socketRef = useRef<SibillSocket | null>(null);

  const connect = useCallback((token: string) => {
    socketRef.current = createSocket(token);
    return socketRef.current.connect();
  }, []);
  const disconnect = useCallback(() => socketRef.current?.disconnect(), []);
  const joinChannel = useCallback(
    (channelName: string, params?: object) => socketRef.current?.joinChannel(channelName, params),
    [],
  );

  const value = useMemo(
    () => ({
      connect,
      disconnect,
      joinChannel,
    }),
    [connect, disconnect, joinChannel],
  );

  return <SibillSocketContext.Provider value={value}>{children}</SibillSocketContext.Provider>;
};

export const useSibillSocket = () => useContext(SibillSocketContext);
